import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Toaster } from '../components/ui/toaster';
import { FileText, Shield, UserCheck, CreditCard, AlertCircle, Lock, Globe, XCircle, Scale, Mail } from 'lucide-react';

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <Toaster />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-orange-50 via-white to-red-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-100 to-red-100 text-orange-700 px-5 py-2.5 rounded-full text-sm font-bold mb-8">
            <FileText className="w-4 h-4" />
            Terms and Conditions
          </div>
          <h1 className="text-5xl lg:text-7xl font-black mb-8">
            Terms and&nbsp;
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">
              Conditions
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Last Updated: January 2025
          </p>
        </div>
      </section>

      {/* Policy Content */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Introduction */}
          <div className="mb-12 p-8 bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl border-2 border-orange-100">
            <h2 className="text-3xl font-black mb-6">Introduction</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Welcome to our platform. These Terms and Conditions ("Terms") govern your access to and use of our software platform and related services ("Services"). By accessing or using the platform, you agree to comply with and be bound by these Terms. If you do not agree, you must not use our services.
            </p>
          </div>

          {/* 1. Definitions */}
          <div className="mb-12">
            <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
              <FileText className="w-8 h-8 text-orange-600" />
              1. Definitions
            </h2>
            <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
              <p><strong>"We", "Us", "Our"</strong> refers to the platform provider, its owner, and authorized agents.</p>
              <p><strong>"User", "You"</strong> refers to any individual or entity accessing or using the Services.</p>
              <p><strong>"Account"</strong> refers to your registered profile on the platform.</p>
            </div>
          </div>

          {/* 2. Use of Services */}
          <div className="mb-12 p-8 bg-white rounded-2xl border-2 border-gray-200">
            <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
              <UserCheck className="w-8 h-8 text-blue-600" />
              2. Use of Services
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">2.1.</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  You agree to provide accurate, current, and complete registration information and maintain the security of your login credentials.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">2.2.</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  You are responsible for all activities that occur under your account.
                </p>
              </div>
            </div>
          </div>

          {/* 3. Subscription & Payments */}
          <div className="mb-12">
            <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
              <CreditCard className="w-8 h-8 text-green-600" />
              3. Subscription & Payments
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">3.1.</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Access to the platform is provided on a subscription basis. Plans may be monthly, or annually.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">3.2.</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  All payments must be made in full and on time. Failure to pay may result in suspension or termination of your account.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">3.3.</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Refunds and cancellations are subject to our Refund and Cancellation Policy.
                </p>
              </div>
            </div>
          </div>

          {/* 4. User Conduct */}
          <div className="mb-12 p-8 bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl border-2 border-red-100">
            <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
              <AlertCircle className="w-8 h-8 text-red-600" />
              4. User Conduct
            </h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">4.1. You agree not to use the Services:</h3>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-3"></div>
                    <span className="text-lg text-gray-700">For any unlawful, harmful, or fraudulent purpose.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-3"></div>
                    <span className="text-lg text-gray-700">To interfere with or disrupt the platform or servers.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-3"></div>
                    <span className="text-lg text-gray-700">To upload or distribute any viruses or malicious code.</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">4.2.</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  You will not attempt to reverse-engineer, decompile, or copy the source code of our platform.
                </p>
              </div>
            </div>
          </div>

          {/* 5. Data and Privacy */}
          <div className="mb-12">
            <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
              <Lock className="w-8 h-8 text-purple-600" />
              5. Data and Privacy
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">5.1.</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  You retain ownership of your data.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">5.2.</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  By accessing or using the services, you hereby grant us a license to utilize your profile data for the purposes of operating, maintaining, and securing the services.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">5.3.</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Your data is handled in accordance with our Privacy Policy.
                </p>
              </div>
            </div>
          </div>

          {/* 6. Intellectual Property */}
          <div className="mb-12 p-8 bg-white rounded-2xl border-2 border-gray-200">
            <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
              <Shield className="w-8 h-8 text-indigo-600" />
              6. Intellectual Property
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">6.1.</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  All content, software, design, and trademarks are the property of the platform or its licensors.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">6.2.</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  You are granted a limited, non-exclusive, non-transferable license to use the platform solely for your internal business purposes.
                </p>
              </div>
            </div>
          </div>

          {/* 7. Service Availability */}
          <div className="mb-12">
            <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
              <Globe className="w-8 h-8 text-cyan-600" />
              7. Service Availability
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">7.1.</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  While we aim to provide consistently high availability, we are not liable for disruptions resulting from events beyond our control, including acts of God or other unavoidable circumstances.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">7.2.</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  We may suspend or terminate the services for maintenance or in case of security or legal concerns.
                </p>
              </div>
            </div>
          </div>

          {/* 8. Termination */}
          <div className="mb-12 p-8 bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl border-2 border-red-100">
            <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
              <XCircle className="w-8 h-8 text-red-600" />
              8. Termination
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">8.1.</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  We may suspend or terminate your access for violations of these Terms or applicable laws.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">8.2.</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Upon termination, your access will be revoked, and your data may be deleted after a retention period.
                </p>
              </div>
            </div>
          </div>

          {/* 9. Limitation of Liability */}
          <div className="mb-12">
            <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
              <Shield className="w-8 h-8 text-yellow-600" />
              9. Limitation of Liability
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">9.1.</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  The platform is provided "as-is" without any warranties, express or implied.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">9.2.</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  We are not liable for indirect, incidental, or consequential damages arising out of your use of the service.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">9.3.</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Our total liability under these Terms shall not exceed the amount paid by you in the previous 3 months.
                </p>
              </div>
            </div>
          </div>

          {/* 10. Governing Law */}
          <div className="mb-12 p-8 bg-white rounded-2xl border-2 border-gray-200">
            <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
              <Scale className="w-8 h-8 text-gray-700" />
              10. Governing Law
            </h2>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">10.1.</h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                These Terms are governed by the laws of [Insert Jurisdiction, e.g., India], without regard to conflict of law principles. Any disputes will be subject to the exclusive jurisdiction of the courts in [Insert City, e.g., Pune, Maharashtra].
              </p>
            </div>
          </div>

          {/* 11. Changes to Terms */}
          <div className="mb-12">
            <h2 className="text-3xl font-black mb-6">11. Changes to Terms</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              We reserve the right to update these Terms at any time. Changes will be notified via email or the platform. Continued use of the Services after such updates constitutes your agreement to the revised Terms.
            </p>
          </div>

          {/* 12. Contact Us */}
          <div className="mb-12 p-8 bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl text-white">
            <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
              <Mail className="w-8 h-8" />
              12. Contact Us
            </h2>
            <p className="text-lg leading-relaxed mb-6">
              For any questions or concerns regarding these Terms, please contact us at:
            </p>
            <div className="space-y-3">
              <p className="text-lg">
                <strong>Email:</strong>{' '}
                <a href="mailto:support@aalign.io" className="text-orange-100 hover:text-white font-semibold underline">
                  support@aalign.io
                </a>
              </p>
              <p className="text-lg">
                <strong>Website:</strong>{' '}
                <a href="https://www.aalign.io" className="text-orange-100 hover:text-white font-semibold underline">
                  www.aalign.io
                </a>
              </p>
            </div>
          </div>

          {/* Contact CTA */}
          <div className="mt-16 p-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl text-center border-2 border-gray-200">
            <h3 className="text-2xl font-bold mb-4 text-gray-900">Have Questions About Our Terms?</h3>
            <p className="text-lg mb-6 text-gray-600">
              Our support team is here to help clarify any aspect of our Terms and Conditions.
            </p>
            <a
              href="mailto:support@aalign.io"
              className="inline-block bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold px-8 py-3 rounded-xl shadow-lg transition-all transform hover:scale-105"
            >
              Contact Support
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsAndConditions;
